/**
 * Site timezone helpers.
 *
 * Sites carry a NEM region code (NSW1, VIC1, QLD1, SA1, ...). Times must be
 * rendered in the SITE's timezone, not the browser's — a site in SA (UTC+9:30)
 * or QLD (no DST) viewed from a browser elsewhere would otherwise be shown at
 * the wrong wall-clock time. Mirrors shared/utils/timezones.py on the backend.
 */

const REGION_TZ: Record<string, string> = {
  NSW: 'Australia/Sydney',
  ACT: 'Australia/Sydney',
  VIC: 'Australia/Melbourne',
  TAS: 'Australia/Hobart',
  QLD: 'Australia/Brisbane', // no DST
  SA: 'Australia/Adelaide', // UTC+9:30
  WA: 'Australia/Perth', // no DST
  NT: 'Australia/Darwin', // no DST
};

export const DEFAULT_TIMEZONE = 'Australia/Sydney';

/** Map a NEM region/state code ("VIC1", "nsw", "QLD") to an IANA timezone. */
export function regionToTimezone(region?: string | null): string {
  if (!region) return DEFAULT_TIMEZONE;
  const key = region.toUpperCase().replace(/[0-9]+$/, '');
  return REGION_TZ[key] ?? DEFAULT_TIMEZONE;
}

/** Format an ISO timestamp as HH:MM in the given IANA timezone (24h). */
export function formatTimeInTz(timestamp: string, tz: string): string {
  const date = new Date(timestamp);
  return new Intl.DateTimeFormat('en-AU', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: tz,
  }).format(date);
}

/** Hour-of-day (with fractional minutes) for an ISO timestamp in a timezone. */
export function getLocalHourInTz(timestamp: string, tz: string): number {
  const date = new Date(timestamp);
  const parts = new Intl.DateTimeFormat('en-AU', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: tz,
  }).formatToParts(date);
  const hh = Number(parts.find((p) => p.type === 'hour')?.value ?? '0');
  const mm = Number(parts.find((p) => p.type === 'minute')?.value ?? '0');
  return hh + mm / 60;
}
